<template>
  <div>
    <div class="page-header">
      <h1>📊 支出分析</h1>
      <p>圖解你的消費結構</p>
    </div>

    <!-- 期間選擇 -->
    <div class="period-bar">
      <button v-for="p in periods" :key="p.key"
        :class="['period-btn', { active: activePeriod === p.key }]"
        @click="selectPeriod(p)">
        {{ p.icon }} {{ p.label }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="empty-state">
      <div class="spinner"></div>
      <p>資料載入中...</p>
    </div>

    <template v-else-if="hasData">
      <!-- 統計卡 -->
      <div class="stats-row">
        <div class="stat-card">
          <div class="stat-label">總支出</div>
          <div class="stat-value expense">${{ formatNum(totalExpense) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">筆數</div>
          <div class="stat-value">{{ txCount }} 筆</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">平均每筆</div>
          <div class="stat-value">${{ formatNum(avgAmount) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">最大單筆</div>
          <div class="stat-value expense">${{ formatNum(maxAmount) }}</div>
        </div>
      </div>

      <!-- 圖表區 -->
      <div class="charts-grid">

        <!-- 1. 類別支出分布（玫瑰圖） -->
        <div class="chart-card">
          <div class="chart-title">🏷️ 類別支出分布</div>
          <div class="chart-subtitle">{{ activePeriodLabel }}</div>
          <v-chart class="chart" :option="categoryOption" autoresize />
        </div>

        <!-- 2. 付款方式分布（甜甜圈） -->
        <div class="chart-card">
          <div class="chart-title">💳 付款方式分布</div>
          <div class="chart-subtitle">{{ activePeriodLabel }}</div>
          <v-chart class="chart" :option="accountOption" autoresize />
        </div>

        <!-- 3. 支出金額排行（橫條） -->
        <div class="chart-card chart-card-wide">
          <div class="chart-title">📋 類別金額排行</div>
          <div class="chart-subtitle">由高至低（Top 10）</div>
          <v-chart class="chart chart-bar" :option="barOption" autoresize />
        </div>

        <!-- 4. 月收支趨勢 -->
        <div class="chart-card chart-card-wide">
          <div class="chart-title">📅 近期月收支</div>
          <div class="chart-subtitle">本月 / 上個月 / 前兩個月</div>
          <v-chart class="chart chart-bar" :option="monthlyOption" autoresize />
        </div>

      </div>
    </template>

    <!-- Empty -->
    <div v-else class="empty-state">
      <div class="empty-icon">📭</div>
      <h3>暫無資料</h3>
      <p>請選擇上方的期間查看分析</p>
    </div>
  </div>
</template>

<script>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { PieChart, BarChart, LineChart } from 'echarts/charts';
import {
  TitleComponent, TooltipComponent, LegendComponent,
  GridComponent, DatasetComponent
} from 'echarts/components';
import VChart from 'vue-echarts';
import axios from 'axios';
axios.defaults.baseURL = 'https://mb.kman.idv.tw:9579/';

use([
  CanvasRenderer, PieChart, BarChart, LineChart,
  TitleComponent, TooltipComponent, LegendComponent,
  GridComponent, DatasetComponent
]);

// 調色盤
const PALETTE = [
  '#4f46e5','#7c3aed','#db2777','#dc2626','#ea580c',
  '#d97706','#059669','#0891b2','#2563eb','#0d9488',
  '#65a30d','#ca8a04','#9333ea','#e11d48','#0284c7'
];

export default {
  components: { VChart },
  data() {
    return {
      loading: false,
      activePeriod: null,
      activePeriodLabel: '',
      transactions: [],      // 目前期間的流水帳
      monthlySummary: [],    // monthly_financial_summary
      periods: [
        { key: 'today',       icon: '📅', label: '本日',    month: 0 },
        { key: 'thismonth',   icon: '📆', label: '本月',    month: 1 },
        { key: 'lastmonth',   icon: '◀️', label: '上個月',  month: 2 },
        { key: 'last2months', icon: '⏪', label: '前兩個月', month: 3 }
      ]
    };
  },
  computed: {
    hasData() {
      return this.transactions.length > 0;
    },
    // ── 基礎聚合 ──
    totalExpense() {
      return this.transactions.reduce((s, r) => s + this.parseNum(r[3]), 0);
    },
    txCount() {
      return this.transactions.length;
    },
    avgAmount() {
      return this.txCount ? Math.round(this.totalExpense / this.txCount) : 0;
    },
    maxAmount() {
      return this.transactions.reduce((m, r) => Math.max(m, this.parseNum(r[3])), 0);
    },

    // 按類別加總
    byCategory() {
      const map = {};
      this.transactions.forEach(r => {
        const cat = r[2] || '其他';
        map[cat] = (map[cat] || 0) + this.parseNum(r[3]);
      });
      return Object.entries(map)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
    },

    // 按付款方式加總
    byAccount() {
      const map = {};
      this.transactions.forEach(r => {
        const acc = r[4] || '其他';
        map[acc] = (map[acc] || 0) + this.parseNum(r[3]);
      });
      return Object.entries(map)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
    },

    // ── ECharts options ──

    // 1. 類別玫瑰圖
    categoryOption() {
      const data = this.byCategory;
      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'item',
          formatter: p => `${p.name}<br/>$${p.value.toLocaleString()}　${p.percent}%`
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'center',
          textStyle: { fontSize: 12, color: '#374151' },
          formatter: name => name.length > 5 ? name.substring(0, 5) + '…' : name
        },
        color: PALETTE,
        series: [{
          type: 'pie',
          radius: ['20%', '65%'],
          center: ['38%', '50%'],
          roseType: 'area',
          itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
          label: { show: false },
          emphasis: {
            label: { show: true, fontSize: 14, fontWeight: 'bold' },
            itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.2)' }
          },
          data
        }]
      };
    },

    // 2. 付款方式甜甜圈
    accountOption() {
      const data = this.byAccount;
      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'item',
          formatter: p => `${p.name}<br/>$${p.value.toLocaleString()}　${p.percent}%`
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'center',
          textStyle: { fontSize: 12, color: '#374151' },
          formatter: name => name.length > 6 ? name.substring(0, 6) + '…' : name
        },
        color: PALETTE,
        series: [{
          type: 'pie',
          radius: ['40%', '68%'],
          center: ['38%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 3 },
          label: {
            show: true,
            position: 'center',
            formatter: () => `$${this.formatNum(this.totalExpense)}`,
            fontSize: 16,
            fontWeight: 'bold',
            color: '#1f2937'
          },
          emphasis: {
            label: {
              show: true,
              formatter: p => `${p.name}\n${p.percent}%`,
              fontSize: 14
            },
            itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.2)' }
          },
          data
        }]
      };
    },

    // 3. 類別橫條圖（Top 10）
    barOption() {
      const top10 = this.byCategory.slice(0, 10).reverse();
      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: p => `${p[0].name}<br/>$${p[0].value.toLocaleString()}`
        },
        grid: { left: 80, right: 30, top: 10, bottom: 10, containLabel: false },
        xAxis: {
          type: 'value',
          axisLabel: { formatter: v => `$${(v/1000).toFixed(0)}k`, fontSize: 11, color: '#9ca3af' },
          splitLine: { lineStyle: { color: '#f3f4f6' } }
        },
        yAxis: {
          type: 'category',
          data: top10.map(d => d.name),
          axisLabel: { fontSize: 12, color: '#374151' },
          axisTick: { show: false }
        },
        color: PALETTE,
        series: [{
          type: 'bar',
          data: top10.map((d, i) => ({
            value: d.value,
            itemStyle: { color: PALETTE[i % PALETTE.length], borderRadius: [0, 4, 4, 0] }
          })),
          label: {
            show: true,
            position: 'right',
            formatter: p => `$${p.value.toLocaleString()}`,
            fontSize: 11,
            color: '#6b7280'
          },
          barMaxWidth: 28
        }]
      };
    },

    // 4. 月收支趨勢（分組橫條）
    monthlyOption() {
      const rows = this.monthlySummary.filter(r => String(r[0]).toLowerCase() !== 'total');
      const labels   = rows.map(r => r[0]);
      const expenses = rows.map(r => Math.abs(this.parseNum(r[1])));
      const incomes  = rows.map(r => Math.abs(this.parseNum(r[2])));
      const profits  = rows.map(r => this.parseNum(r[3]));

      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: params => {
            const lines = params.map(p => `${p.marker}${p.seriesName}：$${p.value.toLocaleString()}`);
            return `${params[0].axisValue}<br/>${lines.join('<br/>')}`;
          }
        },
        legend: {
          data: ['支出','收入','損益'],
          top: 0,
          textStyle: { fontSize: 12, color: '#374151' }
        },
        grid: { left: 16, right: 16, top: 36, bottom: 0, containLabel: true },
        xAxis: {
          type: 'category',
          data: labels,
          axisLabel: { fontSize: 12, color: '#374151' },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          axisLabel: { formatter: v => `$${(v/1000).toFixed(0)}k`, fontSize: 11, color: '#9ca3af' },
          splitLine: { lineStyle: { color: '#f3f4f6' } }
        },
        color: ['#dc2626','#059669','#4f46e5'],
        series: [
          {
            name: '支出', type: 'bar', data: expenses, barMaxWidth: 36,
            itemStyle: { borderRadius: [4,4,0,0], color: '#dc2626' },
            label: { show: true, position: 'top', formatter: p => `$${(p.value/1000).toFixed(1)}k`, fontSize: 10, color: '#6b7280' }
          },
          {
            name: '收入', type: 'bar', data: incomes, barMaxWidth: 36,
            itemStyle: { borderRadius: [4,4,0,0], color: '#059669' },
            label: { show: true, position: 'top', formatter: p => `$${(p.value/1000).toFixed(1)}k`, fontSize: 10, color: '#6b7280' }
          },
          {
            name: '損益', type: 'line', data: profits,
            symbol: 'circle', symbolSize: 7,
            lineStyle: { color: '#4f46e5', width: 2 },
            itemStyle: { color: '#4f46e5' },
            label: { show: false }
          }
        ]
      };
    }
  },
  mounted() {
    this.selectPeriod(this.periods[1]); // 預設本月
  },
  methods: {
    parseNum(v) {
      if (v === null || v === undefined || v === '') return 0;
      if (typeof v === 'number') return v;
      const str = String(v).replace(/[$,]/g, '').trim();
      const n = parseFloat(str);
      return isNaN(n) ? 0 : n;
    },
    formatNum(v) {
      return Math.round(v).toLocaleString();
    },
    async selectPeriod(p) {
      this.activePeriod = p.key;
      this.activePeriodLabel = p.label;
      this.loading = true;
      this.transactions = [];
      try {
        const headers = { Authorization: `Bearer ${this.$store.getters.setToken}` };
        const [txRes, summaryRes] = await Promise.all([
          axios.get(`getmonth/${p.month}`, { headers }),
          axios.get('getmonth/4', { headers })
        ]);
        const rows = txRes.data.transaction || [];
        this.transactions = rows.filter(r => String(r[2]).toLowerCase() !== 'total');
        this.monthlySummary = summaryRes.data.transaction || [];
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    }
  }
};
</script>

<style scoped>
.page-header { margin-bottom: 20px; }
.page-header h1 { font-size: 24px; font-weight: 700; }
.page-header p  { font-size: 14px; color: var(--text-light); margin-top: 2px; }

/* ── Period bar ── */
.period-bar {
  display: flex; flex-wrap: wrap; gap: 6px;
  background: var(--card); border-radius: var(--radius);
  box-shadow: var(--shadow); padding: 10px 12px; margin-bottom: 16px;
}
.period-btn {
  padding: 8px 16px; font-size: 13px; font-weight: 600;
  background: #f9fafb; border: 1px solid var(--border); border-radius: 8px;
  cursor: pointer; color: var(--text); transition: all 0.15s;
}
.period-btn:hover  { border-color: var(--primary); color: var(--primary); background: #eef2ff; }
.period-btn.active { background: var(--primary); color: #fff; border-color: var(--primary); }

/* ── Stats row ── */
.stats-row {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;
  margin-bottom: 16px;
}
.stat-card {
  background: var(--card); border-radius: var(--radius);
  box-shadow: var(--shadow); padding: 14px 16px; text-align: center;
}
.stat-label { font-size: 12px; color: var(--text-light); margin-bottom: 4px; }
.stat-value { font-size: 20px; font-weight: 700; color: var(--text); }
.stat-value.expense { color: #dc2626; }

/* ── Charts grid ── */
.charts-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}
.chart-card {
  background: var(--card); border-radius: var(--radius);
  box-shadow: var(--shadow); padding: 16px 20px;
}
.chart-card-wide { grid-column: span 2; }
.chart-title   { font-size: 15px; font-weight: 700; color: var(--text); }
.chart-subtitle { font-size: 12px; color: var(--text-light); margin-bottom: 8px; }

.chart     { height: 300px; }
.chart-bar { height: 260px; }

/* ── Empty ── */
.empty-state {
  background: var(--card); border-radius: var(--radius);
  box-shadow: var(--shadow); padding: 48px 20px; text-align: center;
}
.empty-icon { font-size: 48px; margin-bottom: 12px; }
.empty-state h3 { font-size: 18px; font-weight: 600; margin-bottom: 4px; }
.empty-state p  { font-size: 14px; color: var(--text-light); }

.spinner {
  width: 32px; height: 32px; border: 3px solid var(--border);
  border-top-color: var(--primary); border-radius: 50%;
  animation: spin 0.8s linear infinite; margin: 0 auto 12px;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Responsive ── */
@media (max-width: 640px) {
  .stats-row { grid-template-columns: repeat(2, 1fr); }
  .charts-grid { grid-template-columns: 1fr; }
  .chart-card-wide { grid-column: span 1; }
  .chart     { height: 260px; }
  .chart-bar { height: 220px; }
}
</style>
